import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  LogIn,
  ArrowLeft,
  Shield,
  Clock,
  Wrench,
  FileText,
  Bell,
  HelpCircle,
  MessageCircle,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";

const portalFeatures = [
  {
    icon: Wrench,
    title: "Manutenções",
    description: "Acompanhe o histórico e agende novas manutenções",
    gradient: "from-cyan-500 to-blue-500",
  },
  {
    icon: FileText,
    title: "Orçamentos",
    description: "Visualize orçamentos enviados e aprovados",
    gradient: "from-blue-500 to-indigo-500",
  },
  {
    icon: Shield,
    title: "PMOC",
    description: "Acesse documentos e laudos do seu PMOC",
    gradient: "from-indigo-500 to-purple-500",
  },
  {
    icon: FileText,
    title: "Notas Fiscais",
    description: "Consulte notas fiscais e comprovantes",
    gradient: "from-purple-500 to-pink-500",
  },
  {
    icon: HelpCircle,
    title: "Suporte",
    description: "Abra chamados e acompanhe atendimentos",
    gradient: "from-cyan-500 to-teal-500",
  },
  {
    icon: Bell,
    title: "Notificações",
    description: "Fique por dentro de avisos e lembretes",
    gradient: "from-teal-500 to-green-500",
  },
];

export function ClientArea() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);


  const [loginError, setLoginError] = useState<string | null>(null);
  const { login, logout, isLoading, error, isAuthenticated, isAdmin, clearError } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      if (isAdmin) {
        // Admin tentando acessar área do cliente - faz logout
        logout();
        setLoginError("Use a Área Administrativa para acessar o painel de gestão");
      } else {
        // Cliente autenticado, redireciona para portal
        window.location.href = "/portal";
      }
    }
  }, [isAuthenticated, isAdmin, logout]);

  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);

    try {
      const result = await login(email, password);
      if (result?.user?.role === "admin") {
        logout();
        setLoginError("Use a Área Administrativa para acessar o painel de gestão");
      } else {
        // Usa window.location para garantir redirecionamento
        window.location.href = "/portal";
      }
    } catch {
      // Erro já tratado no store
    }
  };

  const displayError = loginError || error;


  return (
    <>
      <Helmet>
        <title>Área do Cliente | Arcgel</title>
        <meta
          name="description"
          content="Acesse a área exclusiva do cliente Arcgel. Gerencie manutenções, orçamentos, PMOC e muito mais."
        />
      </Helmet>

      <div className="min-h-screen pt-20">
        {/* Hero Section com Login */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          {/* Background decorativo */}
          <div className="absolute inset-0 z-0">
            <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-radial from-cyan-500/10 to-transparent rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-gradient-radial from-blue-500/10 to-transparent rounded-full blur-3xl" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Lado Esquerdo - Informações */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar para Home
                  <span className="ml-2 px-3 py-1 text-xs font-medium bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-full text-cyan-400">
                    Área Exclusiva
                  </span>
                </Link>

                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  Portal do{" "}
                  <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                    Cliente
                  </span>
                </h1>

                <p className="text-lg text-slate-300 mb-8 max-w-md">
                  Acesse sua área exclusiva para gerenciar manutenções, 
                  orçamentos, documentos PMOC e muito mais.
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                      <Shield className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">Acesso Seguro</h3>
                      <p className="text-sm text-slate-400">
                        Seus dados protegidos com criptografia de ponta a ponta
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                      <Clock className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">Suporte 24/7</h3>
                      <p className="text-sm text-slate-400">
                        Equipe disponível para atender suas necessidades
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Lado Direito - Formulário de Login */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex justify-center lg:justify-end"
              >
                <div className="w-full max-w-md p-8 rounded-2xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 shadow-2xl">
                  <div className="text-center mb-8">
                    <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 mb-4">
                      <LogIn className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-white">
                      Acesse sua conta
                    </h2>
                    <p className="text-slate-400 text-sm mt-2">
                      Entre com suas credenciais para acessar o portal
                    </p>
                  </div>

                  {displayError && (
                    <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2 text-red-400">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm">{displayError}</span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-slate-300 mb-2"
                      >
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={isLoading}
                        placeholder="seu@email.com"
                        className="w-full px-4 py-3 rounded-lg bg-slate-900/50 border border-slate-600 text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors disabled:opacity-50"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label
                          htmlFor="password"
                          className="block text-sm font-medium text-slate-300"
                        >
                          Senha
                        </label>
                        <button
                          type="button"
                          className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                        >
                          Esqueceu a senha?
                        </button>
                      </div>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          id="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          disabled={isLoading}
                          placeholder="••••••••"
                          className="w-full px-4 py-3 rounded-lg bg-slate-900/50 border border-slate-600 text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors disabled:opacity-50 pr-12"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-3 px-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Entrando...
                        </>
                      ) : (
                        <>
                          Entrar
                          <LogIn className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </form>

                  <p className="text-center text-slate-400 text-sm mt-6">
                    Não tem acesso?{" "}
                    <Link
                      to="/contato"
                      className="text-cyan-400 hover:text-cyan-300 transition-colors font-medium"
                    >
                      Entre em contato
                    </Link>
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>


        {/* Seção de Funcionalidades do Portal */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                O que você encontra no{" "}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  portal
                </span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Tudo que você precisa em um só lugar
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {portalFeatures.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group p-6 rounded-xl bg-slate-800/30 border border-slate-700/50 hover:border-cyan-500/30 transition-all duration-300"
                >
                  <div
                    className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${feature.gradient} mb-4 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-400 text-sm">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA de Suporte */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl mx-auto text-center p-8 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50"
            >
              <h3 className="text-2xl font-bold text-white mb-3">
                Precisa de ajuda?
              </h3>
              <p className="text-slate-400 mb-6">
                Nossa equipe está pronta para atender você
              </p>
              <a
                href="https://wa.me/5513991028221"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg border border-white/20 transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5" />
                Falar com Suporte
              </a>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
